import React from 'react';
import { CareerFocusWithMetrics } from '../../hooks/useCareerFocus';
import { useInternshipResults } from '../../hooks/useInternshipResults';
import { useDarkMode } from '../../context/DarkModeContext';
import { TrophyIcon, DocumentTextIcon, CalendarIcon } from '@heroicons/react/24/outline';
import { formatDate } from '../../utils/dateUtils';
import ResultsEmptyState from './Results/ResultsEmptyState';
import ResultsSummary from './Results/ResultsSummary';
import ResultsTabSkeleton from './Results/ResultsTabSkeleton';

interface ResultsTabProps {
  focus: CareerFocusWithMetrics;
}

const ResultsTab: React.FC<ResultsTabProps> = ({ focus }) => {
  const { darkMode } = useDarkMode();
  const { result, loading, error, refresh } = useInternshipResults(focus.id);
  const [initialLoad, setInitialLoad] = React.useState(true);

  React.useEffect(() => {
    if (!loading && initialLoad) {
      setInitialLoad(false);
    }
  }, [loading, initialLoad]);

  // Show skeleton only on initial load
  if (loading && initialLoad) {
    return <ResultsTabSkeleton />;
  }

  if (error) {
    const textColor = darkMode ? 'text-gray-400' : 'text-gray-600';
    return (
      <div className={`text-center py-12 ${textColor}`}>
        <p>Error loading results: {error.message}</p>
      </div>
    );
  }

  if (!result) {
    return <ResultsEmptyState focus={focus} onRefresh={refresh} />;
  }

  return <ResultsSummary result={result} focus={focus} onRefresh={refresh} />;
};

export default ResultsTab;